import React, { useEffect, useState } from "react";
import { Card, Typography, Badge, Divider, Image } from "antd";
import { TServiceTicket } from "~/types";
import { CheckCircle, Clock, User, UserCheck } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useGetInventories } from "~/services/inventory";
import { useQueryClient } from "@tanstack/react-query";

dayjs.extend(utc);

const { Text, Title } = Typography;

interface ServiceCompletionDetailsProps {
  serviceTicket: TServiceTicket;
  showTitle?: boolean;
}

export const ServiceCompletionDetails: React.FC<ServiceCompletionDetailsProps> = ({
  serviceTicket,
  showTitle = true,
}) => {
  const [confirmedInventory, setConfirmedInventory] = useState(0);
  const queryClient = useQueryClient();

  // Fetch inventories with history to get confirmed counts
  const { data: inventoriesData, refetch: refetchInventories } = useGetInventories({
    serviceTicketId: serviceTicket.id,
    pageSize: 500,
  });

  // Calculate confirmed inventory count when data loads
  useEffect(() => {
    if (inventoriesData?.items) {
      const confirmed = inventoriesData.items.filter((item) => {
        // Check if item has history with checked=true for this service ticket
        if (!item.history || !Array.isArray(item.history)) {
          return false;
        }
        const historyEntry = item.history.find(
          (history) =>
            history.serviceTicketId === serviceTicket.id &&
            history.inventoryId === item.id &&
            history.checked === true
        );
        return !!historyEntry;
      }).length;
      setConfirmedInventory(confirmed);
    } else if (serviceTicket.id) {
      // If no data yet, try to refetch
      refetchInventories();
    }
  }, [inventoriesData, serviceTicket.id, refetchInventories]);

  // Refetch when service ticket completion status changes
  useEffect(() => {
    if (serviceTicket.completedAt || serviceTicket.completed_at) {
      // Refetch inventories to get updated history
      refetchInventories();
      // Also invalidate inventory queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
    }
  }, [serviceTicket.completedAt, serviceTicket.completed_at, refetchInventories, queryClient]);

  if (!serviceTicket.completedAt && !serviceTicket.completed_at) {
    return null;
  }

  const formatDateTime = (date: Date | string) => {
    // Parse the UTC timestamp but keep the same hour/minute values
    // Don't convert to local timezone - display exactly what was stored
    const d = dayjs.utc(date);
    return {
      date: d.format('MMM D, YYYY'),
      time: d.format('h:mm A'),
    };
  };

  const timeIn = serviceTicket.timeIn ? formatDateTime(serviceTicket.timeIn) : null;
  const timeOut = serviceTicket.timeOut ? formatDateTime(serviceTicket.timeOut) : null;
  const completedAt = formatDateTime(serviceTicket.completedAt || serviceTicket.completed_at!);

  // Calculate inventory counts
  const totalInventory = serviceTicket.inventories?.length || 0;

  return (
    <Card size="small" className="mt-4">
      {showTitle && (
        <div className="flex items-center mb-4">
          <CheckCircle className="text-green-600 mr-2" size={20} />
          <Title level={5} className="mb-0">Service Completion Details</Title>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column: Time Information */}
        <div>
          <div className="flex items-center mb-3">
            <Clock className="text-blue-600 mr-2" size={16} />
            <Text strong>Time Information</Text>
          </div>

          <div className="space-y-2 pl-6">
            {timeIn && (
              <div>
                <Text type="secondary" className="text-sm">Time In:</Text>
                <div className="font-medium">
                  {timeIn.date} at {timeIn.time}
                </div>
              </div>
            )}

            {timeOut && (
              <div>
                <Text type="secondary" className="text-sm">Time Out:</Text>
                <div className="font-medium">
                  {timeOut.date} at {timeOut.time}
                </div>
              </div>
            )}

            <div>
              <Text type="secondary" className="text-sm">Completed:</Text>
              <div className="font-medium">
                {completedAt.date} at {completedAt.time}
              </div>
            </div>

            {timeIn && timeOut && (
              <div>
                <Text type="secondary" className="text-sm">Duration:</Text>
                <div className="font-medium">
                  {(() => {
                    const start = new Date(serviceTicket.timeIn!);
                    const end = new Date(serviceTicket.timeOut!);
                    const diffMs = end.getTime() - start.getTime();
                    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                    return `${diffHours}h ${diffMinutes}m`;
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Services Performed */}
          {(serviceTicket.addOnServices?.length || 0) > 0 && (
            <div className="mt-6">
              <div className="flex items-center mb-3">
                <CheckCircle className="text-purple-600 mr-2" size={16} />
                <Text strong>Services Performed</Text>
              </div>

              <div className="space-y-2 pl-6">
                {serviceTicket.addOnServices?.map((service, index) => (
                  <div key={service.id} className="text-sm">
                    <div className="flex items-center gap-2">
                      <Text strong>{index + 1}. {service.name}</Text>
                      {service.status === 'completed' && (
                        <Badge status="success" text="Completed" />
                      )}
                      {service.status === 'removed' && (
                        <Badge status="error" text="Removed from Scope" />
                      )}
                      {service.status === 'pending' && (
                        <Badge status="default" text="Pending" />
                      )}
                    </div>
                    {service.description && (
                      <div className="text-gray-600 ml-4 mt-1">{service.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Confirmation Status */}
        <div>
          <div className="flex items-center mb-3">
            <UserCheck className="text-green-600 mr-2" size={16} />
            <Text strong>Confirmation Status</Text>
          </div>

          <div className="space-y-3 pl-6">
            {serviceTicket.staffCheckoff && (
              <Badge status="success" text="Staff confirmed completion" />
            )}

            {serviceTicket.adminOverride && (
              <Badge status="success" text="Admin authorized completion" />
            )}

            {totalInventory > 0 && (
              <div>
                <Text type="secondary" className="text-sm">Inventory Items:</Text>
                <div className="font-medium">
                  {confirmedInventory} of {totalInventory} confirmed
                </div>
              </div>
            )}

            {(serviceTicket.addOnServices?.length || 0) > 0 && (
              <div>
                <Text type="secondary" className="text-sm">Add-On Services:</Text>
                <div className="font-medium">
                  {serviceTicket.addOnServices?.filter(s => s.status === 'completed').length || 0} of {serviceTicket.addOnServices?.length || 0} completed
                </div>
              </div>
            )}

            {serviceTicket.completionNotes && (
              <div>
                <Text type="secondary" className="text-sm">Completion Notes:</Text>
                <div className="mt-1 p-2 bg-gray-50 rounded text-sm">
                  {serviceTicket.completionNotes}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Divider />

      {/* Signatures Section */}
      <div>
        <div className="flex items-center mb-4">
          <User className="text-purple-600 mr-2" size={16} />
          <Text strong>Signatures</Text>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Client Signature */}
          {serviceTicket.clientSignature && (
            <div>
              <Text type="secondary" className="text-sm block mb-2">
                Client Signature
              </Text>
              <div className="border rounded-lg p-3 bg-gray-50">
                <Image
                  src={serviceTicket.clientSignature}
                  alt="Client Signature"
                  width="100%"
                  height={120}
                  style={{ objectFit: 'contain' }}
                  preview={{
                    mask: 'View Signature'
                  }}
                />
                {serviceTicket.clientFirstName && (
                  <Text className="text-sm mt-2 block text-center font-medium">
                    {serviceTicket.clientFirstName}
                  </Text>
                )}
              </div>
            </div>
          )}

          {/* Job Lead Signature */}
          {serviceTicket.jobLeadSignature && (
            <div>
              <Text type="secondary" className="text-sm block mb-2">
                Job Lead Signature
              </Text>
              <div className="border rounded-lg p-3 bg-gray-50">
                <Image
                  src={serviceTicket.jobLeadSignature}
                  alt="Job Lead Signature"
                  width="100%"
                  height={120}
                  style={{ objectFit: 'contain' }}
                  preview={{
                    mask: 'View Signature'
                  }}
                />
                {serviceTicket.jobLeadFirstName && (
                  <Text className="text-sm mt-2 block text-center font-medium">
                    {serviceTicket.jobLeadFirstName}
                  </Text>
                )}
              </div>
            </div>
          )}
        </div>

        {!serviceTicket.clientSignature && !serviceTicket.jobLeadSignature && (
          <Text type="secondary" className="text-sm">
            No signatures recorded for this service.
          </Text>
        )}
      </div>
    </Card>
  );
}; 